/**
 * Deep-linking helpers for the marketing site.
 *
 * The app registers universal links for run.cards/{meeting,club,user}/* AND a
 * custom scheme `runcards://`. The marketing site is served from run.cards
 * itself, and iOS will NOT fire a Universal Link to the same domain you're
 * already on — so from here we use the custom scheme, which opens the app
 * regardless of origin. If the app isn't installed, the scheme does nothing and
 * we invoke `onFallback` (e.g. show the download prompt) after a short beat.
 *
 * Path format matches mobile App.js `linking.config.screens`:
 *   meeting/:meetingId · club/:clubId · user/:userId
 */

/** Build the app deep-link URL for a card. */
export const appDeepLink = (type, id) =>
  type && id ? `runcards://${type}/${encodeURIComponent(id)}` : null;

/** Best-effort check for a phone/tablet where the app could be installed. */
export const isMobileDevice = () => {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  if (/android|iphone|ipad|ipod/i.test(ua)) return true;
  // iPadOS 13+ reports as Mac; disambiguate via touch points.
  if (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1) return true;
  return false;
};

/**
 * Try to open the app at a specific run/club; fall back if it isn't installed.
 *
 * @param {Object} opts
 * @param {'meeting'|'club'|'user'} opts.type
 * @param {string|number} opts.id
 * @param {() => void} [opts.onFallback] invoked on desktop, or on mobile when
 *        the app didn't take over within the timeout (i.e. not installed).
 */
export function openInAppOrFallback({ type, id, onFallback }) {
  const url = appDeepLink(type, id);

  // No id, or desktop (the scheme does nothing useful there) → straight to prompt.
  if (!url || !isMobileDevice()) {
    onFallback?.();
    return;
  }

  let settled = false;
  const settle = (didOpen) => {
    if (settled) return;
    settled = true;
    cleanup();
    if (!didOpen) onFallback?.();
  };

  // If the app launches, this page is backgrounded — cancel the fallback.
  const onVisibility = () => { if (document.visibilityState === "hidden") settle(true); };
  const onPageHide = () => settle(true);
  const onBlur = () => settle(true);
  const cleanup = () => {
    document.removeEventListener("visibilitychange", onVisibility);
    window.removeEventListener("pagehide", onPageHide);
    window.removeEventListener("blur", onBlur);
  };

  document.addEventListener("visibilitychange", onVisibility);
  window.addEventListener("pagehide", onPageHide);
  window.addEventListener("blur", onBlur);

  // Trigger the app.
  try {
    window.location.href = url;
  } catch {
    settle(false);
    return;
  }

  // Still here after a beat → app not installed → fall back.
  setTimeout(() => settle(false), 1500);
}
